import { useState, useRef } from 'react'
import { productosAPI, inventarioAPI } from '../services/api'
import EscanerCodigo from '../components/EscanerCodigo'
import toast from 'react-hot-toast'

export default function Recepcion() {
  const [busqueda, setBusqueda]     = useState('')
  const [resultados, setResultados] = useState([])
  const [lineas, setLineas]         = useState([])   // [{ id, nombre, stock, cantidad }]
  const [observacion, setObservacion] = useState('')
  const [guardando, setGuardando]   = useState(false)
  const [ultimaRecepcion, setUltimaRecepcion] = useState(null)
  const [escaneando, setEscaneando] = useState(false)
  const [scanResultado, setScanResultado] = useState(null)
  const busquedaRef = useRef()

  // La cámara detectó un código: suma 1 unidad a la línea del producto
  const codigoDetectado = async codigo => {
    try {
      const { data } = await productosAPI.listar({ q: codigo, limit: 5 })
      const encontrados = data.data
      const exacto = encontrados.find(p => p.codigo_barra === codigo) || (encontrados.length === 1 ? encontrados[0] : null)
      if (exacto) {
        const p = exacto
        setLineas(prev => {
          const ex = prev.find(l => l.id === p.id)
          if (ex) return prev.map(l => l.id === p.id ? {...l, cantidad: (parseFloat(l.cantidad)||0) + 1} : l)
          return [...prev, { id: p.id, nombre: p.nombre, stock: p.stock, cantidad: 1 }]
        })
        setScanResultado({ tipo: 'ok', texto: `${p.nombre} +1`, k: Date.now() })
      } else if (encontrados.length > 1) {
        setEscaneando(false)
        buscar(codigo)
      } else {
        setScanResultado({ tipo: 'error', texto: 'Código no registrado. Agrégalo en Productos.', k: Date.now() })
      }
    } catch {
      setScanResultado({ tipo: 'error', texto: 'No se pudo verificar el código. Intenta de nuevo.', k: Date.now() })
    }
  }

  const buscar = async q => {
    setBusqueda(q)
    if (q.trim().length < 2) { setResultados([]); return }
    try {
      const { data } = await productosAPI.listar({ q, limit: 8 })
      setResultados(data.data)
    } catch {}
  }

  const agregar = p => {
    setLineas(prev => {
      const ex = prev.find(l => l.id === p.id)
      if (ex) return prev.map(l => l.id === p.id ? {...l, cantidad: (parseFloat(l.cantidad)||0) + 1} : l)
      return [...prev, { id: p.id, nombre: p.nombre, stock: p.stock, cantidad: 1 }]
    })
    setBusqueda('')
    setResultados([])
    busquedaRef.current?.focus()
  }

  // Enter con un solo resultado = agregar (así funciona el lector de código de barras)
  const handleEnter = e => {
    if (e.key === 'Enter' && resultados.length === 1) agregar(resultados[0])
  }

  const cambiarCantidad = (id, valor) => {
    setLineas(prev => prev.map(l => l.id === id ? {...l, cantidad: valor} : l))
  }

  const quitar = id => setLineas(prev => prev.filter(l => l.id !== id))

  const totalUnidades = lineas.reduce((s, l) => s + (parseFloat(l.cantidad) || 0), 0)

  const confirmar = async () => {
    if (!lineas.length) { toast.error('Agrega al menos un producto'); return }
    if (lineas.some(l => !parseFloat(l.cantidad) || parseFloat(l.cantidad) <= 0)) {
      toast.error('Revisa las cantidades: deben ser mayores a 0'); return
    }
    setGuardando(true)
    try {
      const { data } = await inventarioAPI.recepcion({
        items: lineas.map(l => ({ producto_id: l.id, cantidad: parseFloat(l.cantidad) })),
        observacion: observacion.trim() || undefined
      })
      toast.success(`✅ Recepción registrada: ${data.unidades} unidades`)
      setUltimaRecepcion({ lineas: [...lineas], observacion, unidades: data.unidades })
      setLineas([])
      setObservacion('')
      busquedaRef.current?.focus()
    } catch {} finally { setGuardando(false) }
  }

  return (
    <div style={{padding:24, maxWidth:820}}>
      <div className="section-title" style={{marginBottom:4}}>Recepción de productos</div>
      <p style={{fontSize:13, color:'var(--color-muted)', marginBottom:16}}>
        Registra la mercadería que llega: busca o escanea cada producto, indica cuántas
        unidades llegaron y confirma. El stock se actualiza automáticamente.
      </p>

      {/* Buscador */}
      <div style={{position:'relative', marginBottom:16}}>
        <div style={{display:'flex', gap:8}}>
          <input
            ref={busquedaRef}
            className="form-input"
            placeholder="🔍  Buscar o escanear producto..."
            value={busqueda}
            autoFocus
            onChange={e => buscar(e.target.value)}
            onKeyDown={handleEnter}
            style={{fontSize:14, padding:'11px 14px', flex:1}}
          />
          <button className="btn btn-secondary" onClick={() => setEscaneando(true)}
            title="Escanear con la cámara" style={{padding:'0 14px'}}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2"/>
              <line x1="7" y1="8" x2="7" y2="16"/><line x1="10.5" y1="8" x2="10.5" y2="16"/>
              <line x1="13.5" y1="8" x2="13.5" y2="16"/><line x1="17" y1="8" x2="17" y2="16"/>
            </svg>
          </button>
        </div>
        {resultados.length > 0 && (
          <div style={{
            position:'absolute', top:'100%', left:0, right:0, zIndex:50,
            background:'var(--color-surface)', border:'1px solid var(--color-border-md)',
            borderRadius:'var(--radius-md)', marginTop:4, boxShadow:'var(--shadow-md)',
            maxHeight:280, overflowY:'auto'
          }}>
            {resultados.map(p => (
              <button key={p.id} onClick={() => agregar(p)}
                style={{
                  display:'flex', justifyContent:'space-between', alignItems:'center',
                  width:'100%', padding:'10px 14px', background:'none', border:'none',
                  borderBottom:'1px solid var(--color-border)', cursor:'pointer',
                  fontSize:13, textAlign:'left', fontFamily:'var(--font)'
                }}>
                <span>{p.nombre}</span>
                <span style={{color:'var(--color-muted)', fontSize:12}}>Stock: {p.stock}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Líneas de recepción */}
      {lineas.length > 0 ? (
        <div className="table-wrap" style={{marginBottom:16}}>
          <table>
            <thead>
              <tr><th>Producto</th><th>Stock actual</th><th style={{width:130}}>Llegaron</th><th style={{width:50}}></th></tr>
            </thead>
            <tbody>
              {lineas.map(l => (
                <tr key={l.id}>
                  <td style={{fontWeight:500}}>{l.nombre}</td>
                  <td style={{color:'var(--color-muted)'}}>{l.stock}</td>
                  <td>
                    <input className="form-input" type="number" min="0" step="any"
                      value={l.cantidad}
                      onChange={e => cambiarCantidad(l.id, e.target.value)}
                      style={{padding:'6px 10px', fontWeight:600}}/>
                  </td>
                  <td>
                    <button onClick={() => quitar(l.id)} title="Quitar"
                      style={{background:'none', border:'none', cursor:'pointer', color:'var(--color-danger)', fontSize:16}}>
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state" style={{padding:'32px 20px', marginBottom:16, border:'1px dashed var(--color-border-md)', borderRadius:'var(--radius-md)'}}>
          <h3>Sin productos aún</h3>
          <p>Busca o escanea los productos que llegaron</p>
        </div>
      )}

      {/* Observación + confirmar */}
      <div className="form-group">
        <label className="form-label">Observación (opcional)</label>
        <input className="form-input" value={observacion}
          onChange={e => setObservacion(e.target.value)}
          placeholder="Ej: Factura N° 1234 — Proveedor Los Andes"/>
      </div>

      <button className="btn btn-primary btn-lg btn-block" onClick={confirmar}
        disabled={guardando || !lineas.length}>
        {guardando ? <span className="spinner"/> : `Confirmar recepción (${totalUnidades} unidades)`}
      </button>

      {/* ══ Escáner con cámara ══ */}
      {escaneando && (
        <EscanerCodigo
          onDetectado={codigoDetectado}
          onCerrar={() => { setEscaneando(false); setScanResultado(null) }}
          resultado={scanResultado}
        />
      )}

      {/* Resumen de la última recepción */}
      {ultimaRecepcion && (
        <div className="card" style={{marginTop:20, borderLeft:'4px solid var(--color-success)'}}>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start'}}>
            <div>
              <div style={{fontWeight:700, marginBottom:6}}>✅ Última recepción registrada</div>
              {ultimaRecepcion.lineas.map(l => (
                <div key={l.id} style={{fontSize:13, color:'var(--color-muted)'}}>
                  {l.nombre}: <strong style={{color:'var(--color-success)'}}>+{l.cantidad}</strong>
                </div>
              ))}
              {ultimaRecepcion.observacion && (
                <div style={{fontSize:12, color:'var(--color-hint)', marginTop:6}}>
                  {ultimaRecepcion.observacion}
                </div>
              )}
            </div>
            <button onClick={() => setUltimaRecepcion(null)}
              style={{background:'none', border:'none', cursor:'pointer', fontSize:16, color:'var(--color-muted)'}}>✕</button>
          </div>
        </div>
      )}
    </div>
  )
}
